'use client'

import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { MONETIZATION_ENABLED } from '@/monetization-switch'
import { supabase } from '@/lib/supabase'
import {
  getPublicPricing,
  normalizeBillingStatus,
  resolveAccountEntitlement,
  type AccountEntitlementRecord,
  type AppPlan,
  type BillingStatus,
  type EntitlementSource,
  type PlanLimits
} from '@/lib/monetization'

export type Entitlements = {
  plan: AppPlan
  isPremium: boolean
  limits: PlanLimits
  paywallsEnabled: boolean
  premiumChaptersEnabled: boolean
  dodoBillingEnabled: boolean
  billingStatus: BillingStatus
  entitlementSource: EntitlementSource
  refreshEntitlements: () => Promise<void>
  pricing: {
    weeklyUsd: number
    yearlyUsd: number
  }
}

export function useEntitlements(): Entitlements {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const dodoBillingEnabled = MONETIZATION_ENABLED

  const billingQuery = useQuery({
    queryKey: ['billing-subscription-status', user?.id],
    enabled: dodoBillingEnabled && Boolean(user?.id),
    queryFn: async (): Promise<BillingStatus> => {
      if (!user?.id) return 'none'

      const { data, error } = await supabase
        .from('billing_subscriptions')
        .select('status')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) {
        // When table isn't deployed yet, keep app functional with metadata fallback.
        if (error.code === '42P01' || error.code === 'PGRST205') {
          return 'none'
        }
        console.warn('Failed to fetch billing status:', error)
        return 'unknown'
      }

      const status = data?.status
      if (typeof status !== 'string' || !status.trim()) {
        return 'none'
      }

      return normalizeBillingStatus(status)
    },
    staleTime: 30_000
  })

  const entitlementQuery = useQuery({
    queryKey: ['account-entitlement', user?.id],
    enabled: MONETIZATION_ENABLED && Boolean(user?.id),
    queryFn: async (): Promise<AccountEntitlementRecord | null> => {
      if (!user?.id) return null

      const canonical = await supabase
        .from('user_entitlements')
        .select('entitlement,active,status,source,current_period_end')
        .eq('user_id', user.id)
        .eq('entitlement', 'pro')
        .eq('active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!canonical.error && canonical.data) {
        return {
          plan: canonical.data.active ? 'pro' : 'free',
          source:
            canonical.data.source === 'admin_grant'
              ? 'admin_grant'
              : canonical.data.source === 'none'
                ? 'none'
                : 'subscription',
          status: canonical.data.active ? 'active' : 'inactive',
          expires_at: canonical.data.current_period_end
        }
      }
      if (
        canonical.error &&
        canonical.error.code !== '42P01' &&
        canonical.error.code !== 'PGRST205'
      ) {
        console.warn('Failed to fetch user entitlement:', canonical.error)
      }

      const { data, error } = await supabase
        .from('account_entitlements')
        .select('plan,source,status,expires_at')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error) {
        if (error.code === '42P01' || error.code === 'PGRST205') {
          return null
        }
        console.warn('Failed to fetch account entitlement:', error)
        return null
      }

      return data as AccountEntitlementRecord | null
    },
    staleTime: 30_000
  })

  const refreshEntitlements = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['billing-subscription-status', user?.id]
      }),
      queryClient.invalidateQueries({
        queryKey: ['account-entitlement', user?.id]
      })
    ])
    await Promise.all([billingQuery.refetch(), entitlementQuery.refetch()])
  }, [billingQuery, entitlementQuery, queryClient, user?.id])

  return useMemo(() => {
    const paywallsEnabled = MONETIZATION_ENABLED
    const premiumChaptersEnabled = MONETIZATION_ENABLED

    const billingStatus = dodoBillingEnabled
      ? (billingQuery.data ?? 'none')
      : 'none'
    const entitlement = resolveAccountEntitlement({
      record: entitlementQuery.data,
      paywallsEnabled
    })

    return {
      plan: entitlement.plan,
      isPremium: entitlement.isPremium,
      limits: entitlement.limits,
      paywallsEnabled,
      premiumChaptersEnabled,
      dodoBillingEnabled,
      billingStatus,
      entitlementSource: entitlement.source,
      refreshEntitlements,
      pricing: getPublicPricing()
    }
  }, [
    dodoBillingEnabled,
    billingQuery.data,
    entitlementQuery.data,
    refreshEntitlements
  ])
}
